import { useTranslations } from "next-intl";
import { Navigate } from "react-router-dom";
import { useSearchParams } from "@/hooks/useSearchParams";
import { useQuery } from "@tanstack/react-query";
import { SCard } from "@/components/Card/SCard";
import { Spacing } from "@/components/Spacing/Spacing";
import { CoveringLoadingSpinner } from "@/components/LoadingSpinner/CoveringLoadingSpinner";
import { validateResetToken } from "@/lib/api/handlers/AuthenticationHandling";
import { AuthLayout } from "./AuthLayout";
import { TimeLeft } from "./change-password/TimeLeft";
import { ChangePasswordForm } from "./change-password/ChangePasswordForm";
import { ChangePasswordError } from "./change-password/ChangePasswordError";

export function ChangePasswordPage() {
  const t = useTranslations("changePassword");
  const search = useSearchParams();
  const token = search.get("token");

  // The hook runs before the tokenless redirect below, gated by `enabled`
  // instead of by an early return: it used to sit AFTER `if (!token) return`,
  // so a render without a token called one hook fewer and React threw
  // "rendered fewer hooks than expected" as soon as the token appeared or
  // vanished mid-mount. `enabled` keeps the request itself from firing.
  const validation = useQuery({
    queryKey: ["validateResetToken", token],
    queryFn: () => validateResetToken(token!),
    retry: false,
    enabled: !!token,
  });

  if (!token) return <Navigate to="/login" replace />;

  if (validation.isLoading) return <AuthLayout><CoveringLoadingSpinner /></AuthLayout>;

  const result = validation.data;
  const isError =
    validation.isError ||
    (result &&
      typeof result === "object" &&
      "isServerError" in result &&
      result.isServerError === true);

  if (isError) {
    return (
      <AuthLayout>
        <SCard header={t("changePasswordTitle")} outline="outline-quaternary" shadow="shadow-xl">
          <Spacing height="h-6" />
          <ChangePasswordError />
        </SCard>
      </AuthLayout>
    );
  }

  const { expiresAt } = result as { email: string; expiresAt: number };
  const timeLeft = Math.floor((expiresAt - Date.now()) / 1000);

  return (
    <AuthLayout>
      <SCard header={t("changePasswordTitle")} outline="outline-quaternary" shadow="shadow-xl">
        <Spacing height="h-6" />
        <TimeLeft timeLeft={timeLeft} />
        <Spacing height="h-6" />
        <ChangePasswordForm token={token} expiresAt={expiresAt} />
      </SCard>
    </AuthLayout>
  );
}
